import { ReactNode } from 'react'
import { Typography, Breadcrumb, BreadcrumbProps } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import { spacing } from '@/config/design-tokens'

const { Title, Text } = Typography

/**
 * PageHeader variant types:
 * - 'simple': Title + Subtitle + Actions (no breadcrumb, no back button)
 * - 'compact': Back Button + Title + Subtitle + Actions (no breadcrumb)
 * - 'full': Breadcrumb + Back Button + Title + Subtitle + Actions
 */
export type PageHeaderVariant = 'simple' | 'compact' | 'full'

/**
 * Status types for the page header status badge
 */
export type PageHeaderStatus = 'draft' | 'live' | 'sent' | 'scheduled' | 'archived' | 'processing'

export interface PageHeaderProps {
  /**
   * Page title (required)
   */
  title: string

  /**
   * Page subtitle or description displayed next to title
   */
  subtitle?: string

  /**
   * Alias for subtitle (backward compatibility)
   */
  description?: string

  /**
   * Breadcrumb items (when provided, variant defaults to 'full')
   */
  breadcrumb?: BreadcrumbProps['items']

  /**
   * Show back button (when true without breadcrumb, variant defaults to 'compact')
   * @default false
   */
  showBackButton?: boolean

  /**
   * Back button handler. If not provided:
   * - With breadcrumb: navigates to parent breadcrumb item
   * - Without breadcrumb: uses browser history.back()
   */
  onBack?: () => void

  /**
   * Action buttons or content displayed on the right
   */
  actions?: ReactNode

  /**
   * Alias for actions (backward compatibility)
   */
  headerAction?: ReactNode

  /**
   * Alias for actions (backward compatibility)
   */
  actionButton?: ReactNode

  /**
   * Tag displayed after title (e.g., custom ReactNode)
   * @deprecated Use `status` prop for status badges
   */
  tag?: ReactNode

  /**
   * Status badge displayed after title (e.g., "Draft", "Live", "Sent")
   * Shows a pill-shaped badge with a status dot
   */
  status?: PageHeaderStatus

  /**
   * Breadcrumb separator
   * @default '/'
   */
  breadcrumbSeparator?: ReactNode

  /**
   * Force a specific variant. If not provided, variant is auto-detected:
   * - Has breadcrumb → 'full'
   * - Has back button, no breadcrumb → 'compact'
   * - No back button, no breadcrumb → 'simple'
   */
  variant?: PageHeaderVariant

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Additional inline styles
   */
  style?: React.CSSProperties
}

/**
 * Status badge configuration
 */
const STATUS_CONFIG: Record<PageHeaderStatus, { label: string; dotColor: string }> = {
  draft: { label: 'Draft', dotColor: '#D9D9D9' },
  live: { label: 'Live', dotColor: '#52C41A' },
  sent: { label: 'Sent', dotColor: '#52C41A' },
  scheduled: { label: 'Scheduled', dotColor: '#1890FF' },
  archived: { label: 'Archived', dotColor: '#8C8C8C' },
  processing: { label: 'Processing', dotColor: '#1890FF' },
}

/**
 * Status Badge Component - renders a pill-shaped badge with status dot
 */
function StatusBadge({ status }: { status: PageHeaderStatus }) {
  const config = STATUS_CONFIG[status]

  return (
    <div
      className="inline-flex items-center gap-2 px-2 rounded-full"
      style={{ backgroundColor: '#F0F0F0', height: '22px' }}
    >
      <div
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: config.dotColor }}
        aria-hidden="true"
      />
      <span
        className="text-sm leading-[22px]"
        style={{ color: 'rgba(0, 0, 0, 0.85)' }}
      >
        {config.label}
      </span>
    </div>
  )
}

/**
 * PageHeader Pattern Component
 *
 * A consistent page header with 3 variants supporting breadcrumbs, back button,
 * title, subtitle, tag, and action buttons.
 *
 * Variant is auto-detected based on props:
 * - Has breadcrumb → 'full'
 * - Has showBackButton, no breadcrumb → 'compact'
 * - Neither → 'simple'
 *
 * @example
 * // Simple variant - Title + Subtitle (auto-detected)
 * <PageHeader
 *   title="Event Settings"
 *   subtitle="Edit event settings such as name and dates."
 * />
 *
 * @example
 * // Simple variant with actions
 * <PageHeader
 *   title="Attendee Management"
 *   subtitle="Create, edit and delete attendees."
 *   actions={<Button variant="primary">Add Attendee</Button>}
 * />
 *
 * @example
 * // Compact variant - Back Button + Title (auto-detected)
 * <PageHeader
 *   title="Edit Template"
 *   subtitle="This is a subtitle"
 *   showBackButton
 *   onBack={() => router.visit('/templates')}
 * />
 *
 * @example
 * // Full variant - Breadcrumb + Back Button + Title + Status (auto-detected)
 * <PageHeader
 *   title="Create Manual Email"
 *   status="draft"
 *   breadcrumb={[
 *     { title: 'Email Blast', href: '/messaging/email-blast' },
 *     { title: 'Create' }
 *   ]}
 *   showBackButton
 * />
 */
export function PageHeader({
  title,
  subtitle,
  description,
  breadcrumb,
  showBackButton = false,
  onBack,
  actions,
  headerAction,
  actionButton,
  tag,
  status,
  breadcrumbSeparator = '/',
  variant,
  className = '',
  style,
}: PageHeaderProps) {
  // Resolve aliases for backward compatibility
  const displaySubtitle = subtitle || description
  const displayActions = actions || headerAction || actionButton

  // Auto-detect variant if not explicitly provided
  const resolvedVariant: PageHeaderVariant = variant || (
    breadcrumb && breadcrumb.length > 0
      ? 'full'
      : showBackButton
        ? 'compact'
        : 'simple'
  )

  /**
   * Smart back navigation:
   * 1. If onBack is provided, use it
   * 2. If breadcrumb exists, navigate to parent breadcrumb item
   * 3. Otherwise, use browser history.back()
   */
  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }

    if (breadcrumb && breadcrumb.length >= 2) {
      const parentBreadcrumb = breadcrumb[breadcrumb.length - 2]

      if (parentBreadcrumb) {
        if (parentBreadcrumb.onClick) {
          parentBreadcrumb.onClick(undefined as unknown as React.MouseEvent<HTMLAnchorElement | HTMLSpanElement>)
        } else if (parentBreadcrumb.href) {
          // Use Inertia router if available
          if (typeof window !== 'undefined' && (window as unknown as { Inertia?: { visit: (url: string) => void } }).Inertia) {
            (window as unknown as { Inertia: { visit: (url: string) => void } }).Inertia.visit(parentBreadcrumb.href)
          } else {
            window.location.href = parentBreadcrumb.href
          }
          return
        }
      }
    }

    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back()
    }
  }

  // Back button component
  const BackButton = () => (
    <button
      onClick={handleBack}
      className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity bg-transparent border-none p-0"
      aria-label="Go back"
      type="button"
    >
      <LeftOutlined
        style={{
          fontSize: '16px',
          color: 'rgba(0, 0, 0, 0.85)',
        }}
      />
    </button>
  )

  // Title section with optional status badge or tag
  const TitleSection = () => (
    <div className="flex items-center gap-3">
      <Title
        level={4}
        className="m-0! font-medium! leading-7! text-lg!"
        style={{
          fontSize: '20px',
          color: 'rgba(0, 0, 0, 0.85)',
        }}
      >
        {title}
      </Title>
      {/* Status badge (preferred) or custom tag (deprecated) */}
      {status && <StatusBadge status={status} />}
      {!status && tag && <span>{tag}</span>}
      {displaySubtitle && (
        <Text
          className="text-sm!"
          style={{ color: 'rgba(0, 0, 0, 0.45)' }}
        >
          {displaySubtitle}
        </Text>
      )}
    </div>
  )

  // Actions section
  const ActionsSection = () => displayActions ? (
    <div className="flex items-center gap-2">
      {displayActions}
    </div>
  ) : null

  return (
    <div
      className={`bg-white w-full ${className}`}
      style={{
        paddingLeft: spacing.xl,
        paddingRight: spacing.xl,
        paddingTop: spacing.md,
        paddingBottom: spacing.md,
        ...style,
      }}
    >
      {/* Breadcrumb Section - only for 'full' variant */}
      {resolvedVariant === 'full' && breadcrumb && breadcrumb.length > 0 && (
        <Breadcrumb
          separator={breadcrumbSeparator}
          items={breadcrumb}
          className="[&_.ant-breadcrumb-link]:text-sm [&_.ant-breadcrumb-link]:leading-[22px] [&_.ant-breadcrumb-separator]:text-sm [&_.ant-breadcrumb-separator]:leading-[22px]"
        />
      )}

      {/* Title Row */}
      <div
        className="flex items-center justify-between"
        style={{
          paddingTop: resolvedVariant === 'full' ? '14px' : '6px',
          paddingBottom: '6px',
        }}
      >
        <div className="flex items-center gap-4">
          {/* Back Button - for 'compact' and 'full' variants when showBackButton is true */}
          {(resolvedVariant === 'compact' || resolvedVariant === 'full') && showBackButton && <BackButton />}

          {/* Title + Tag + Subtitle */}
          <TitleSection />
        </div>

        {/* Actions - right aligned */}
        <ActionsSection />
      </div>
    </div>
  )
}
